// Small chrome primitives for backoffice pages: KPI tile, Sparkline,
// BarChart, PageHeader, Avatar, Toolbar children, EmptyHint. Each is a
// near-direct port of the cardrive admin-shell.jsx equivalent, retyped
// into idiomatic TypeScript React.

import type { ReactNode } from 'react';
import { AdminIcon } from './AdminIcon';

// ─── KPI ─────────────────────────────────────────────────────────────

export interface KPIProps {
  label: string;
  value: string;
  delta?: string;
  hint?: string;
  accent?: string;
}

export function KPI({ label, value, delta, hint, accent }: KPIProps) {
  const up = typeof delta === 'string' ? delta.startsWith('+') : true;
  const tone = up ? '#0f7c5b' : '#b42318';
  return (
    <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-4">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--bo-mute)]">
          {label}
        </div>
        {accent ? (
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: accent }} />
        ) : null}
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <div className="text-[26px] font-semibold tracking-tight tabular-nums">{value}</div>
        {delta ? (
          <div className="text-[12px] font-medium tabular-nums" style={{ color: tone }}>
            {delta}
          </div>
        ) : null}
      </div>
      {hint ? (
        <div className="mt-1 text-[11.5px] text-[color:var(--bo-mute)]">{hint}</div>
      ) : null}
    </div>
  );
}

// ─── Sparkline ───────────────────────────────────────────────────────

export interface SparklineProps {
  data: number[];
  color?: string;
  height?: number;
  area?: boolean;
}

export function Sparkline({
  data,
  color = 'var(--bo-accent)',
  height = 36,
  area = true,
}: SparklineProps) {
  if (data.length < 2) return null;
  const w = 120;
  const h = height;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = max - min || 1;
  const pts = data.map(
    (v, i) =>
      [
        (i / (data.length - 1)) * w,
        h - ((v - min) / span) * (h - 4) - 2,
      ] as const,
  );
  const d = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ height }}>
      {area ? <path d={`${d} L${w},${h} L0,${h} Z`} fill={color} opacity="0.12" /> : null}
      <path d={d} fill="none" stroke={color} strokeWidth="1.6" />
    </svg>
  );
}

// ─── BarChart ────────────────────────────────────────────────────────

export interface BarChartKey {
  key: string;
  color: string;
}

export interface BarChartProps {
  data: Array<Record<string, string | number>>;
  keys: BarChartKey[];
  labelKey: string;
}

export function BarChart({ data, keys, labelKey }: BarChartProps) {
  if (data.length === 0) return null;
  const max = Math.max(
    ...data.flatMap((row) => keys.map((k) => Number(row[k.key] ?? 0))),
  );
  return (
    // items-stretch (the default), NOT items-end: items-end shrink-wraps each
    // column to its content, so the bar row collapsed to ~2px and every bar's
    // `height: <pct>%` resolved against that — rendering a flat 2px line
    // regardless of value. The bars are bottom-aligned by the inner row's own
    // items-end, which is where that belongs.
    <div className="flex h-full gap-3">
      {data.map((row, i) => {
        const label = String(row[labelKey] ?? i);
        return (
          <div key={`${label}-${i}`} className="flex flex-1 flex-col items-stretch gap-1">
            <div className="flex flex-1 items-end gap-1">
              {keys.map((k) => {
                const value = Number(row[k.key] ?? 0);
                const pct = max ? (value / max) * 100 : 0;
                return (
                  <div
                    key={k.key}
                    className="flex-1 rounded-t-sm"
                    style={{ height: `${pct}%`, background: k.color, minHeight: 2 }}
                    title={`${k.key}: ${value.toLocaleString()}`}
                  />
                );
              })}
            </div>
            <div className="text-center text-[10px] text-[color:var(--bo-mute)]">{label}</div>
          </div>
        );
      })}
    </div>
  );
}

// ─── PageHeader ──────────────────────────────────────────────────────

export interface PageHeaderProps {
  eyebrow?: string;
  title: string;
  sub?: string;
  actions?: ReactNode;
}

export function PageHeader({ eyebrow, title, sub, actions }: PageHeaderProps) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 border-b border-[color:var(--bo-line)] pb-4 sm:gap-4 sm:pb-5">
      <div className="min-w-0">
        {eyebrow ? (
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[color:var(--bo-mute)]">
            {eyebrow}
          </div>
        ) : null}
        <h1 className="mt-1 text-[20px] font-semibold tracking-tight sm:text-[26px]">
          {title}
        </h1>
        {sub ? (
          <p className="mt-1 max-w-2xl text-[12.5px] text-[color:var(--bo-mute)] sm:text-[13.5px]">
            {sub}
          </p>
        ) : null}
      </div>
      {actions ? (
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">{actions}</div>
      ) : null}
    </div>
  );
}

// ─── Avatar ──────────────────────────────────────────────────────────

const AVATAR_PALETTE = ['#fde68a', '#bae6fd', '#ddd6fe', '#bbf7d0', '#fecaca', '#fbcfe8', '#fed7aa'];

export interface AvatarProps {
  name: string | null | undefined;
  size?: number;
  color?: string;
  /** Optional image URL. When set, renders an <img>; otherwise falls back to
   *  the colored-initials avatar. Pass an already-resolved absolute URL —
   *  this component doesn't know about Payload's `/api/media/file/…` prefix. */
  src?: string | null;
}

export function Avatar({ name, size = 28, color, src }: AvatarProps) {
  const safeName = name && name.trim().length > 0 ? name : '?';
  if (src) {
    return (
      <img
        src={src}
        alt={safeName}
        className="rounded-full object-cover"
        style={{ width: size, height: size }}
      />
    );
  }
  const initials = safeName
    .split(' ')
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  const bg =
    color ?? AVATAR_PALETTE[safeName.charCodeAt(0) % AVATAR_PALETTE.length];
  return (
    <span
      className="inline-grid place-items-center rounded-full font-semibold text-[color:var(--bo-ink)]"
      style={{ width: size, height: size, background: bg, fontSize: size * 0.42 }}
    >
      {initials}
    </span>
  );
}

// ─── Toolbar ─────────────────────────────────────────────────────────

export function Toolbar({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-center gap-2">{children}</div>;
}

export interface ToolSearchProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}

export function ToolSearch({ value, onChange, placeholder = 'Search…' }: ToolSearchProps) {
  return (
    <div className="relative w-full sm:w-auto">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--bo-mute)]">
        <AdminIcon name="search" />
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-9 w-full rounded-lg border border-[color:var(--bo-line)] bg-white pl-9 pr-3 text-[13px] outline-none focus:border-[color:var(--bo-accent)] sm:w-[260px]"
      />
    </div>
  );
}

export interface ToolChipProps {
  active?: boolean;
  onClick?: () => void;
  children: ReactNode;
  count?: number;
}

export function ToolChip({ active, onClick, children, count }: ToolChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-[13px] font-medium ${
        active
          ? 'border-[color:var(--bo-ink)] bg-[color:var(--bo-ink)] text-white'
          : 'border-[color:var(--bo-line)] bg-white text-[color:var(--bo-ink)] hover:border-[color:var(--bo-mute)]'
      }`}
    >
      {children}
      {count != null ? (
        <span className={`tabular-nums text-[11px] ${active ? 'text-white/70' : 'text-[color:var(--bo-mute)]'}`}>
          {count}
        </span>
      ) : null}
    </button>
  );
}

export interface ToolBtnProps {
  onClick?: () => void;
  children: ReactNode;
  primary?: boolean;
  icon?: Parameters<typeof AdminIcon>[0]['name'];
  disabled?: boolean;
  /** Native tooltip — useful for explaining a disabled (coming-soon) action. */
  title?: string;
}

export function ToolBtn({ onClick, children, primary, icon, disabled, title }: ToolBtnProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-[13px] font-medium disabled:cursor-not-allowed disabled:opacity-50 ${
        primary
          ? 'bg-[color:var(--bo-ink)] text-white hover:bg-black'
          : 'border border-[color:var(--bo-line)] bg-white text-[color:var(--bo-ink)] hover:border-[color:var(--bo-mute)]'
      }`}
    >
      {icon ? <AdminIcon name={icon} className="h-3.5 w-3.5" /> : null}
      {children}
    </button>
  );
}

// ─── EmptyHint ───────────────────────────────────────────────────────

export function EmptyHint({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-xl border border-dashed border-[color:var(--bo-line)] bg-white px-6 py-10 text-center text-[13px] text-[color:var(--bo-mute)]">
      {children}
    </div>
  );
}
